import React from 'react';
import { Box } from '@mui/material';
import PhotoLikeButton from '../common/PhotoLikeButton';
import type { GalleryPhoto } from './GalleryGrid';

export interface PhotoCardProps {
   photo: GalleryPhoto;
   aspectRatio?: 'square' | 'video' | 'auto';
   onClick: () => void;
   className?: string;
   showLikeButton?: boolean;
   userId?: string;
   albumId?: string;
   likeCount?: number;
   isLiked?: boolean;
   onLikeChange?: (isLiked: boolean, likeCount: number) => void;
}

export default function PhotoCard({
   photo,
   aspectRatio = 'auto',
   onClick,
   className,
   showLikeButton,
   userId,
   albumId,
   likeCount = 0,
   isLiked = false,
   onLikeChange,
}: PhotoCardProps) {
   const src =
      photo.mediaType === 'video'
         ? (photo.thumbnailUrl || '')
         : (photo.thumbnailUrl || photo.url);

   const aspectRatioValue =
      aspectRatio === 'square' ? 1 : aspectRatio === 'video' ? 16 / 9 : undefined;

   return (
      <Box
         className={className}
         onClick={onClick}
         sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            minHeight: 80,
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'pointer',
            backgroundColor: 'grey.200',
            border: '1px solid',
            borderColor: 'grey.300',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            '&:hover': {
               '& .gallery-card-image': {
                  transform: 'scale(1.02)',
               },
               '& .gallery-card-overlay': {
                  opacity: 1,
               },
               '& .gallery-card-like': {
                  opacity: 1,
               },
               boxShadow: 4,
            },
            transition: 'box-shadow 0.2s ease',
         }}
      >
         <Box
            className='gallery-card-image'
            component='img'
            src={src}
            alt={photo.title || ''}
            loading='lazy'
            sx={{
               position: 'absolute',
               inset: 0,
               width: '100%',
               height: '100%',
               objectFit: 'cover',
               transition: 'transform 0.25s ease',
               ...(aspectRatioValue && { aspectRatio: aspectRatioValue }),
            }}
         />
         <Box
            className='gallery-card-overlay'
            sx={{
               position: 'absolute',
               inset: 0,
               bgcolor: 'rgba(0,0,0,0)',
               transition: 'background-color 0.2s ease',
               '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.25)',
               },
            }}
         />
         {showLikeButton && userId && (
            <Box
               className="gallery-card-like"
               onClick={(e) => e.stopPropagation()}
               sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  zIndex: 2,
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
               }}
            >
               <PhotoLikeButton
                  photoId={photo.id}
                  userId={userId}
                  initialLikeCount={likeCount}
                  initialIsLiked={isLiked}
                  albumId={albumId}
                  size="small"
                  variant="compact"
                  showCount
                  onLikeChange={onLikeChange}
               />
            </Box>
         )}
      </Box>
   );
}
